import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";
import { pushToast } from "./lib";

/*
 * เครดิตฟรี — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; หน้าตาแยกกันที่ FreeCredit.js)
 *   • โหลดแคมเปญที่เปิดอยู่ + สถานะรายคน แล้วรีเฟรชเงียบ ๆ ทุก 20 วิ / เมื่อกลับมาที่แท็บ (ให้เห็นสิทธิ์เหลือแบบ real-time)
 *   • กดรับ → เซิร์ฟเวอร์ตัดสินทุกอย่าง (สิทธิ์/ซ้ำ/เต็ม) — ฝั่งนี้แค่แสดงผล
 */

/** ชุดสีของการ์ด — ตรงกับ THEMES ที่แอดมินเลือกในหน้าสร้างแคมเปญ */
export const FC_THEMES = {
  gold: { a: "#FDE047", b: "#F59E0B", ink: "#5B3A00" },
  rose: { a: "#FB7185", b: "#BE123C", ink: "#fff" },
  violet: { a: "#A78BFA", b: "#5B21B6", ink: "#fff" },
  emerald: { a: "#34D399", b: "#047857", ink: "#fff" },
  sky: { a: "#38BDF8", b: "#075985", ink: "#fff" },
  orange: { a: "#FDBA74", b: "#EA580C", ink: "#fff" },
};
export const fcTheme = (key) => FC_THEMES[key] || FC_THEMES.violet;

export const AUD_META = {
  NEW_USER: { label: "สมาชิกใหม่", icon: "solar:user-plus-bold-duotone" },
  DEPOSIT: { label: "ฝากครบรับฟรี", icon: "solar:wallet-money-bold-duotone" },
  ALL: { label: "สำหรับสมาชิกทุกคน", icon: "solar:users-group-rounded-bold-duotone" },
};

// พร้อมรับขึ้นก่อน → ยังไม่ล็อกอิน → ยังไม่เข้าเงื่อนไข → รับแล้ว → เต็ม (คงลำดับเดิมที่แอดมินตั้งในแต่ละกลุ่ม)
const RANK = { CLAIMABLE: 0, LOGIN: 1, NOT_ELIGIBLE: 2, CLAIMED: 3, FULL: 4 };
export const sortFree = (list) =>
  (Array.isArray(list) ? list : [])
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (RANK[a.c.state] ?? 9) - (RANK[b.c.state] ?? 9) || a.i - b.i)
    .map((x) => x.c);

/** ข้อความเงื่อนไขผู้รับสั้น ๆ สำหรับบนการ์ด */
export function requirementText(c) {
  if (c.audience === "NEW_USER") return `สมัครไม่เกิน ${c.new_user_days} วัน`;
  if (c.audience === "DEPOSIT")
    return c.deposit_window === "DAY"
      ? `ฝากครบ ${c.deposit_min.toLocaleString("th-TH")} บาท ภายในวันนี้`
      : `ฝากสะสมครบ ${c.deposit_min.toLocaleString("th-TH")} บาท`;
  return "สมาชิกทุกคนรับได้";
}

/** ป้ายบอกว่าเครดิตนี้แทงได้ที่ไหน — ทุกหวย / เฉพาะ …  (ชื่อมาจาก play_names ที่เซิร์ฟเวอร์ส่งมา) */
export function playChipText(c) {
  const names = c.play_names || [];
  if (c.play_scope !== "CATEGORY" && c.play_scope !== "TYPE") return "แทงได้ทุกหวย";
  if (names.length && names.length <= 2) return `เฉพาะ ${names.join(", ")}`;
  return `เฉพาะ ${names.length} ${c.play_scope === "CATEGORY" ? "หมวด" : "หวย"}`;
}
export const isScoped = (c) => c.play_scope === "CATEGORY" || c.play_scope === "TYPE";

/** หมายเหตุเรื่องยอดเงินคงเหลือ (null = แคมเปญนี้ไม่มีเงื่อนไข) — แสดงบนการ์ดเสมอเมื่อมีเงื่อนไข */
export function balanceNote(c) {
  if (c.max_balance === null || c.max_balance === undefined) return null;
  return Number(c.max_balance) > 0
    ? `* รับได้เฉพาะลูกค้าที่มียอดเงินคงเหลือไม่เกิน ฿${Number(c.max_balance).toLocaleString("th-TH")}`
    : "* รับได้เฉพาะลูกค้าที่ไม่มียอดเงินคงเหลือในกระเป๋า";
}
export const isBalanceBlocked = (c) => c.state === "NOT_ELIGIBLE" && c.block === "BALANCE_HIGH";

/** หมดเขตภายใน 72 ชม. ถึงจะโชว์นับถอยหลัง (ไกลกว่านั้นไม่กระตุ้น) */
export const endingSoon = (c) => {
  if (!c.end_at) return false;
  const ms = new Date(c.end_at).getTime() - Date.now();
  return ms > 0 && ms <= 72 * 3600 * 1000;
};

/** สิทธิ์เหลือน้อย (≤ 20%) */
export const quotaLow = (c) => c.quota > 0 && c.remaining != null && c.remaining / c.quota <= 0.2;

export const depositPct = (c) => {
  const p = c.progress;
  if (!p || p.kind !== "deposit" || !p.need) return 0;
  return Math.max(0, Math.min(100, (p.have / p.need) * 100));
};

const REFRESH_MS = 20000;

export function useFreeCredits(onClaimed) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [items, setItems] = useState(null); // null = ยังโหลดไม่เสร็จ
  const [claimingId, setClaimingId] = useState(null);
  const [result, setResult] = useState(null);
  const alive = useRef(true);
  const busy = useRef(false);
  const cb = useRef(onClaimed);
  cb.current = onClaimed;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    return Promise.resolve()
      .then(() => lottoApi.freeCredits())
      .then((list) => alive.current && setItems(sortFree(list)))
      .catch(() => alive.current && setItems((prev) => prev || []));
    // uid เปลี่ยน (ล็อกอิน/ออก) → โหลดใหม่ด้วย token ใหม่
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    setItems(null);
    load();
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, REFRESH_MS);
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  const claim = useCallback(
    async (c) => {
      if (busy.current) return;
      busy.current = true;
      setClaimingId(c.id);
      try {
        const res = await lottoApi.claimFreeCredit(c.id);
        const d = res?.data || {};
        if (alive.current) {
          setResult({
            id: c.id,
            name: c.name,
            theme: c.theme,
            amount: d.amount ?? c.reward_amount,
            turnover_required: d.turnover_required || 0,
            balance_after: d.balance_after,
            play_label: d.play_scope && d.play_scope !== "ALL" ? d.play_label : "",
          });
        }
        cb.current?.(d);
      } catch (e) {
        pushToast(e?.response?.data?.message || "รับไม่สำเร็จ ลองใหม่อีกครั้ง", "err");
      } finally {
        busy.current = false;
        if (alive.current) setClaimingId(null);
        load();
      }
    },
    [load],
  );

  return {
    items,
    loading: items === null,
    claimingId,
    claim,
    result,
    closeResult: () => setResult(null),
    reload: load,
    isAuthenticated,
  };
}

/**
 * หวยที่เครดิตฟรี (แบบจำกัดหวย) ของลูกค้าใช้แทงไม่ได้ — ให้หน้าแรกทำการ์ดเป็นสีเทา
 * (ยังมีเงินจริงพอ = ไม่เป็นสีเทา · เซิร์ฟเวอร์คำนวณด้วยกฎเดียวกับตอนวางโพย)
 */
export function usePlayLimits() {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [lim, setLim] = useState({ ids: [], labels: [] });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) {
      setLim((p) => (p.ids.length ? { ids: [], labels: [] } : p));
      return Promise.resolve();
    }
    return Promise.resolve()
      .then(() => lottoApi.freeCreditLimits())
      .then((d) => alive.current && setLim({ ids: d?.blocked_type_ids || [], labels: d?.labels || [] }))
      .catch(() => {});
  }, [uid]);

  useEffect(() => {
    load();
    const id = setInterval(() => !document.hidden && load(), REFRESH_MS);
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  return { blocked: new Set(lim.ids), labels: lim.labels, reload: load };
}

/** ข้อความตอนกดหวยที่ใช้เครดิตฟรีแทงไม่ได้ */
export const limitedMessage = (labels) =>
  `เครดิตฟรีของคุณใช้แทงหวยนี้ไม่ได้${labels && labels.length ? ` (ใช้ได้${labels.join(" / ")})` : ""}`;
